import { useMemo } from 'react';
import { IOptionType } from '../@types/helper';
import { useAppSelector } from '../@types/redux';
import { ILot } from '../@types/model/lot/lot';
import { IAccreditation } from '../@types/model/masterData/accreditation/accreditation';
import { IAgreementCode } from '../@types/model/masterData/agreementCode/agreementCode';
import { IBarcodeRange } from '../@types/model/masterData/barcodeRange/barcodeRange';
import { IBrand } from '../@types/model/masterData/brand/brand';
import { ICarrier } from '../@types/model/masterData/carrier/carrier';
import { IColour } from '../@types/model/masterData/colour/colour';
import { ICommodity } from '../@types/model/masterData/commodity/commodity';
import { ICommodityCondition } from '../@types/model/masterData/commodityCondition/commodityCondition';
import { ICommodityState } from '../@types/model/masterData/commodityState/commodityState';
import { IContactInfo } from '../@types/model/masterData/contactInfo/contactInfo';
import { ICountry } from '../@types/model/masterData/country/country';
import { IDepartment } from '../@types/model/masterData/department/department';
import { IDevice } from '../@types/model/masterData/device/device';
import { IDomain } from '../@types/model/masterData/domain/domain';
import { IFarm } from '../@types/model/masterData/farm/farm';
import { IGrade } from '../@types/model/masterData/grade/grade';
import { IInspectionPoint } from '../@types/model/masterData/inspectionPoint/inspectionPoint';
import { IInventory } from '../@types/model/masterData/inventory/inventory';
import { ILotType } from '../@types/model/masterData/lotType/lotType';
import { IMarket } from '../@types/model/masterData/market/market';
import { IMaterial } from '../@types/model/masterData/material/material';
import { IMaterialType } from '../@types/model/masterData/materialType/materialType';
import { IOrchard } from '../@types/model/masterData/orchard/orchard';
import { IOrganization } from '../@types/model/masterData/organization/organization';
import { IOutlet } from '../@types/model/masterData/outlet/outlet';
import { IPack } from '../@types/model/masterData/pack/pack';
import { IPackCategory } from '../@types/model/masterData/packCategory/packCategory';
import { IPackLine } from '../@types/model/masterData/packLine/packLine';
import { IPalletBaseType } from '../@types/model/masterData/palletBaseType/palletBaseType';
import { IPrinter } from '../@types/model/masterData/printer/printer';
import { IPrintServer } from '../@types/model/masterData/printServer/printServer';
import { IProject } from '../@types/model/masterData/project/project';
import { IRegion } from '../@types/model/masterData/region/region';
import { IReport } from '../@types/model/masterData/report/report';
import { ISalesPoint } from '../@types/model/masterData/salesPoint/salesPoint';
import { ISeason } from '../@types/model/masterData/season/season';
import { ISite } from '../@types/model/masterData/site/site';
import { ISiteSetting } from '../@types/model/masterData/siteSetting/siteSetting';
import { ISize } from '../@types/model/masterData/size/size';
import { IStorageUnit } from '../@types/model/masterData/storageUnit/storageUnit';
import { ITruckType } from '../@types/model/masterData/truckType/truckType';
import { IUnitOfMeasure } from '../@types/model/masterData/unitOfMeasure/unitOfMeasure';
import { IVariety } from '../@types/model/masterData/variety/variety';

interface BaseMasterDataItem {
    id : number;
    guid : string;
    isActive : boolean;
    createdBy ?: number;
    createdByName ?: string;
    createdOn ?: string;
    updatedBy ?: number;
    updatedByName ?: string;
    updatedOn ?: string;
}

interface CodeNameItem extends BaseMasterDataItem {
    code : string;
    name : string;
}

interface CodeDescriptionItem extends BaseMasterDataItem {
    code ?: string;
    description ?: string;
}

interface NameItem extends BaseMasterDataItem {
    name ?: string;
}

interface DescriptionItem extends BaseMasterDataItem {
    description ?: string;
}

interface ShortDescriptionItem extends BaseMasterDataItem {
    shortDescription ?: string;
}

interface CodeNameDescriptionItem extends BaseMasterDataItem {
    code ?: string;
    name ?: string;
    description ?: string;
}

interface CodeShortDescriptionItem extends BaseMasterDataItem {
    code ?: string;
    shortDescription ?: string;
}

type MasterDataItem =
    | CodeNameItem
    | CodeDescriptionItem
    | NameItem
    | DescriptionItem
    | ShortDescriptionItem
    | CodeNameDescriptionItem
    | CodeShortDescriptionItem;

type MasterDataType =
    | 'organization'
    | 'site'
    | 'commodity'
    | 'farm'
    | 'variety'
    | 'palletBaseType'
    | 'market'
    | 'brand'
    | 'inventory'
    | 'storageUnit'
    | 'project'
    | 'orchard'
    | 'department'
    | 'carrier'
    | 'truckType'
    | 'materialType'
    | 'lot'
    | 'pack'
    | 'unitOfMeasure'
    | 'accreditation'
    | 'agreementCode'
    | 'barcodeRange'
    | 'colour'
    | 'commodityCondition'
    | 'commodityState'
    | 'contactInfo'
    | 'country'
    | 'device'
    | 'domain'
    | 'grade'
    | 'inspectionPoint'
    | 'lotType'
    | 'material'
    | 'outlet'
    | 'packCategory'
    | 'packLine'
    | 'printer'
    | 'printServer'
    | 'region'
    | 'report'
    | 'salesPoint'
    | 'season'
    | 'size'
    | 'siteSetting';


type ValidFormatForItem<T> = T extends { code : string; name : string } ? 'code-name' | 'code' | 'name' :
    T extends { code : string; shortDescription : string; description : string } ? 'code-description' | 'code-shortDescription' | 'code' | 'shortDescription'  | 'description'  | 'shortDescription-description' :
        T extends { code : string; description : string } ? 'code-description' | 'code' | 'description' :
            T extends { code : string; shortDescription : string } ? 'code-shortDescription' | 'code' | 'shortDescription' :
                T extends { name : string } ? 'name' :
                    T extends { description : string } ? 'description' :
                        T extends { shortDescription : string } ? 'shortDescription' :
                            T extends { category : string } ? 'category' :
                                T extends { condition : string } ? 'condition' :
                                    T extends { code : string } ? 'code' :
                                        T extends { settings : string } ? 'settings' :
                                            T extends { domainName : string } ? 'domainName' :
                                                never;

type TypeToInterface = {
    organization : IOrganization;
    site : ISite;
    market : IMarket;
    brand : IBrand;
    inventory : IInventory;
    storageUnit : IStorageUnit;
    carrier : ICarrier;
    truckType : ITruckType;
    unitOfMeasure : IUnitOfMeasure;
    accreditation : IAccreditation;
    agreementCode : IAgreementCode;
    barcodeRange : IBarcodeRange;
    contactInfo : IContactInfo;
    device : IDevice;
    inspectionPoint : IInspectionPoint;
    material : IMaterial;
    outlet : IOutlet;
    printer : IPrinter;
    printServer : IPrintServer;
    report : IReport;
    salesPoint : ISalesPoint;
    siteSetting : ISiteSetting;
    commodity : ICommodity;
    farm : IFarm;
    variety : IVariety;
    palletBaseType : IPalletBaseType;
    project : IProject;
    orchard : IOrchard;
    materialType : IMaterialType;
    pack : IPack;
    lot : ILot;
    colour : IColour;
    commodityCondition : ICommodityCondition;
    commodityState : ICommodityState;
    country : ICountry;
    domain : IDomain;
    grade : IGrade;
    lotType : ILotType;
    packCategory : IPackCategory;
    packLine : IPackLine;
    region : IRegion;
    season : ISeason;
    size : ISize;
    department : IDepartment;
};

type ValidFormatsForType<T extends MasterDataType> = ValidFormatForItem<TypeToInterface[T]>;

type ValidateLabelFormat<T extends MasterDataType, F extends string> = F extends ValidFormatsForType<T> ? F : never;

const defaultFormats : { [K in MasterDataType] : ValidFormatsForType<K> } = {
    organization: 'code-name',
    site: 'shortDescription-description',
    market: 'code-name',
    brand: 'code-description',
    inventory: 'code-description',
    storageUnit: 'code-description',
    carrier: 'code-name',
    truckType: 'code-description',
    unitOfMeasure: 'code-name',
    accreditation: 'code-name',
    agreementCode: 'code-name',
    barcodeRange: 'name',
    contactInfo: 'name',
    device: 'name',
    inspectionPoint: 'code-name',
    material: 'code-name',
    outlet: 'code',
    printer: 'name',
    printServer: 'name',
    report: 'name',
    salesPoint: 'name',
    siteSetting: 'settings',
    commodity: 'name',
    farm: 'name',
    variety: 'name',
    palletBaseType: 'name',
    project: 'description',
    orchard: 'name',
    materialType: 'name',
    pack: 'code-description',
    lot: 'name',
    colour: 'name',
    commodityCondition: 'condition',
    commodityState: 'name',
    country: 'name',
    domain: 'domainName',
    grade: 'name',
    lotType: 'name',
    packCategory: 'category',
    packLine: 'name',
    region: 'name',
    season: 'name',
    size: 'name',
    department: 'shortDescription',
} as const;

const getDefaultLabelFormat = <T extends MasterDataType>(type : T) : ValidFormatsForType<T> => {
    return defaultFormats[type];
};

export const useMasterDataOptions = <T extends MasterDataType, F extends string>(
    type : T,
    labelFormat ?: ValidateLabelFormat<T, F>,
    filterFn : (item : MasterDataItem) => boolean = x => !!x,
) : Array<IOptionType> => {
    const data = useAppSelector((state) => {
        switch (type) {
            case 'organization':
                return state.masterData.organizations;
            case 'site':
                return state.masterData.sites;
            case 'commodity':
                return state.masterData.commodities;
            case 'farm':
                return state.masterData.farms;
            case 'variety':
                return state.masterData.varieties;
            case 'palletBaseType':
                return state.masterData.palletBaseTypes;
            case 'market':
                return state.masterData.markets;
            case 'brand':
                return state.masterData.brands;
            case 'inventory':
                return state.masterData.inventories;
            case 'storageUnit':
                return state.masterData.storageUnits;
            case 'project':
                return state.masterData.projects;
            case 'orchard':
                return state.masterData.orchards;
            case 'department':
                return state.masterData.departments;
            case 'carrier':
                return state.masterData.carriers;
            case 'truckType':
                return state.masterData.truckTypes;
            case 'materialType':
                return state.masterData.materialTypes;
            case 'lot':
                return state.data.lots;
            case 'pack':
                return state.masterData.packs;
            case 'unitOfMeasure':
                return state.masterData.unitOfMeasures;
            case 'accreditation':
                return state.masterData.accreditations;
            case 'agreementCode':
                return state.masterData.agreementCodes;
            case 'barcodeRange':
                return state.masterData.barcodeRanges;
            case 'colour':
                return state.masterData.colours;
            case 'commodityCondition':
                return state.masterData.commodityConditions;
            case 'commodityState':
                return state.masterData.commodityStates;
            case 'contactInfo':
                return state.masterData.contactInfos;
            case 'country':
                return state.masterData.countries;
            case 'device':
                return state.masterData.devices;
            case 'domain':
                return state.masterData.domains;
            case 'grade':
                return state.masterData.grades;
            case 'inspectionPoint':
                return state.masterData.inspectionPoints;
            case 'lotType':
                return state.masterData.lotTypes;
            case 'material':
                return state.masterData.materials;
            case 'outlet':
                return state.masterData.outlets;
            case 'packCategory':
                return state.masterData.packCategories;
            case 'packLine':
                return state.masterData.packLines;
            case 'printer':
                return state.masterData.printers;
            case 'printServer':
                return state.masterData.printServers;
            case 'region':
                return state.masterData.regions;
            case 'report':
                return state.masterData.reports;
            case 'salesPoint':
                return state.masterData.salesPoints;
            case 'season':
                return state.masterData.seasons;
            case 'size':
                return state.masterData.sizes;
            case 'siteSetting':
                return state.masterData.siteSettings;
            default:
                return undefined;
        }
    });

    const effectiveLabelFormat = labelFormat ?? getDefaultLabelFormat(type);

    return useMemo(() => {
        if (!data) return [];

        return data
            .filter(x => x.isActive && filterFn(x))
            .map((item) => {
                let label = '';

                switch (effectiveLabelFormat) {
                    case 'code-name':
                        if ('code' in item && 'name' in item) {
                            label = `(${item.code}) ${item.name}`;
                        }
                        break;
                    case 'code-description':
                        if ('code' in item && 'description' in item) {
                            label = `(${item.code}) ${item.description}`;
                        }
                        break;
                    case 'shortDescription-description':
                        if ('shortDescription' in item && 'description' in item) {
                            label = `(${item.shortDescription}) ${item.description}`;
                        }
                        break;
                    case 'name':
                        if ('name' in item) {
                            label = item.name as string;
                        }
                        break;
                    case 'shortDescription':
                        if ('shortDescription' in item) {
                            label = item.shortDescription as string;
                        }
                        break;
                    case 'settings':
                        if ('settings' in item) {
                            label = item.settings as string;
                        }
                        break;
                    case 'condition':
                        if ('condition' in item) {
                            label = item.condition as string;
                        }
                        break;
                    case 'category':
                        if ('category' in item) {
                            label = item.category as string;
                        }
                        break;
                    case 'domainName':
                        if ('domainName' in item) {
                            label = item.domainName as string;
                        }
                        break;
                }

                return {
                    label,
                    value: item.id,
                };
            });
    }, [data, effectiveLabelFormat, filterFn]);
};
